import { useWorkingPlatform } from '@repo/platform';
import { useUserEventLogger } from '@repo/tracking-service';
import { Button, Divider, Guide, Title } from '@zenterprise-inc/ui';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { JSX } from 'react';
import { graphql, useFragment } from 'react-relay';

import { IndisSection$key } from '@/__generated__/IndisSection.graphql';
import { setIndisSearchRefundData } from '@/components/tax-refund/common/data/indis-search-refund-data';
import RefundSummaryCard from '@/components/tax-refund/summary/RefundSummaryCard';
import { StatusManualSection } from '@/components/tax-refund/summary/StatusManualSection';
import { APPLYABLE_REFUND_STATUSES, APPLIED_OR_FINISHED_REFUND_STATUSES } from '@/lib/constants/home';
import { COMMON_PATHS, INDIVIDUAL_PATHS } from '@/lib/constants/paths';
import { zCurrency } from '@/lib/utils/zCurrency';

type Props = {
  fragmentRef: IndisSection$key;
  onClickLookup: () => void;
};

const IndisSection = ({ fragmentRef, onClickLookup }: Props) => {
  const { workingPlatform } = useWorkingPlatform();
  const router = useRouter();

  const data = useFragment(
    graphql`
      fragment IndisSection on Query {
        bznavRefund {
          user {
            indis {
              refundHistoryId: id
              refundId
              refundStatus
              refundStage
              refundSubStage
              refundAmount
              refundReportDt
              refundResultDt
              refundResultExpectDt
              paymentClosingDt
              paymentExpectDt
              paymentAmount
              applicationDt
              cancelStage
              userFee
              lostReason
              tin
              office
              snmcSpcTxreAmt
              cenSnmcTxreAmt
              empIncrEtrpTxamtDdcAmt
              intgEmpTxamtDdcAmt
              sctyInfeeTxamtDdcAmt
              reSearchRequestId
              virtualAccountInfo {
                owner
                bank
                account
                paymentAmount
              }
              businessInfo {
                taxpayerName
              }
            }
            previousIndis {
              __typename
            }
            tritxIndis {
              refundId
              refundAmount
              applicationDt
              refundStatus
              refundStage
              tritxReSearchRequestId
              businessInfo {
                taxpayerName
              }
              cancelStage
            }
          }
        }
      }
    `,
    fragmentRef
  );

  const incomeItem = data?.bznavRefund?.user?.indis?.[0];
  const tritxItem = data?.bznavRefund?.user?.tritxIndis?.[0];
  const previousItems = data?.bznavRefund?.user?.previousIndis;

  const isAppliedIncomeRefund = APPLIED_OR_FINISHED_REFUND_STATUSES.includes(incomeItem?.refundStatus ?? '');
  const isAppliedTritxRefund = APPLIED_OR_FINISHED_REFUND_STATUSES.includes(tritxItem?.refundStatus ?? '');

  const isShowNewYearLookupButton = !incomeItem && !tritxItem && (previousItems?.length ?? 0) > 0;
  const isShowIncomeCard = isAppliedIncomeRefund || (isAppliedTritxRefund && APPLYABLE_REFUND_STATUSES.includes(incomeItem?.refundStatus ?? ''));
  const isShowTritxCard = isAppliedTritxRefund || (isAppliedIncomeRefund && APPLYABLE_REFUND_STATUSES.includes(tritxItem?.refundStatus ?? ''));
  const isShowCombinedCard = !isShowIncomeCard && !isShowTritxCard && !isShowNewYearLookupButton;

  const isShowTritxFirst =
    isShowTritxCard && (incomeItem?.refundStatus === 'ApplyComplete' || APPLYABLE_REFUND_STATUSES.includes(tritxItem?.refundStatus ?? ''));

  const isCombinedRetry = isShowCombinedCard && (incomeItem?.refundStatus === 'Retry' || tritxItem?.refundStatus === 'Retry');
  const isIncomeRetry = isShowIncomeCard && incomeItem?.refundStatus === 'Retry';
  const isTritxRetry = isShowTritxCard && tritxItem?.refundStatus === 'Retry';

  const applyableIncomeAmount = Number((incomeItem?.refundStatus === 'ApplyPossible' && incomeItem?.refundAmount) ?? 0);
  const applyableTritxAmount = Number((tritxItem?.refundStatus === 'ApplyPossible' && tritxItem?.refundAmount) ?? 0);

  const setRefundData = () => {
    setIndisSearchRefundData({
      bizName: incomeItem?.businessInfo?.taxpayerName ?? '',
      refundId: incomeItem?.refundId ?? '',
      refundStatus: incomeItem?.refundStatus ?? '',
      refundStage: incomeItem?.refundStage ?? '',
      refundAmount: incomeItem?.refundAmount ?? '',
      snmcSpcTxreAmt: Number(incomeItem?.snmcSpcTxreAmt),
      cenSnmcTxreAmt: Number(incomeItem?.cenSnmcTxreAmt),
      empIncrEtrpTxamtDdcAmt: Number(incomeItem?.empIncrEtrpTxamtDdcAmt),
      intgEmpTxamtDdcAmt: Number(incomeItem?.intgEmpTxamtDdcAmt),
      sctyInfeeTxamtDdcAmt: Number(incomeItem?.sctyInfeeTxamtDdcAmt),
      tin: incomeItem?.tin,
      reSearchRequestId: incomeItem?.reSearchRequestId,
      cancelStage: incomeItem?.cancelStage ?? '',
      tritxRefundInfo: workingPlatform !== 'toss' ? (tritxItem as any) : undefined
    });
  };

  const getCardTitle = (isRetry: boolean, isApplyImpossible: boolean, originAmount: number, applyPossibleAmount?: number, isAppliedRefund?: boolean) => {
    if (isRetry) return '환급금 새로 조회';
    if (isApplyImpossible && originAmount > 0) return '환급 신청이 어려워요';
    if (isAppliedRefund) return zCurrency(originAmount, '원', true, '0');
    return zCurrency(applyPossibleAmount ?? 0, '원', true, '0');
  };

  const getButtonState = (isRetry: boolean, isApplyImpossible: boolean, isAppliedRefund: boolean, isTritx?: boolean) => {
    if (isRetry)
      return {
        title: '다시조회',
        onClick: () => {
          setRefundData();
          onClickLookup();
        }
      };
    if (!isApplyImpossible && !isAppliedRefund)
      return {
        title: '환급받기',
        onClick: () => {
          setRefundData();
          router.push({
            pathname: INDIVIDUAL_PATHS.개인환급액조회결과,
            query: { type: 'indis' }
          });
        }
      };
    if (isApplyImpossible)
      return {
        title: '',
        onClick: () => {
          setRefundData();
          router.push({
            pathname: INDIVIDUAL_PATHS.개인환급액조회결과,
            query: { type: 'indis' }
          });
        }
      };
    if (isAppliedRefund)
      return {
        title: '상세',
        onClick: () => {
          setRefundData();
          router.push({
            pathname: COMMON_PATHS.환급신청결과,
            query: { type: isTritx ? 'indis-tritx' : 'indis' }
          });
        }
      };
    return {
      title: ''
    };
  };

  // 공통(종합소득세 + 양도소득세) 카드
  const combinedCard = (
    <RefundSummaryCard
      cardType="indis"
      cardLabel={incomeItem?.businessInfo?.taxpayerName || tritxItem?.businessInfo?.taxpayerName || ''}
      cardTitle={getCardTitle(
        isCombinedRetry,
        (!incomeItem || incomeItem?.refundStatus === 'ApplyImpossible') && (!tritxItem || tritxItem.refundStatus === 'ApplyImpossible'),
        Number(incomeItem?.refundAmount ?? 0) + Number(tritxItem?.refundAmount ?? 0),
        applyableIncomeAmount + applyableTritxAmount
      )}
      buttonState={getButtonState(
        isCombinedRetry,
        (!incomeItem || incomeItem?.refundStatus === 'ApplyImpossible') && (!tritxItem || tritxItem.refundStatus === 'ApplyImpossible'),
        isAppliedIncomeRefund || isAppliedTritxRefund
      )}
      refundItem={incomeItem}
    />
  );

  // 종합소득세 카드
  const incomeCard = (
    <div className={`rounded-5 bg-white cursor-pointer transition-transform duration-150 ease-in-out hover:scale-[0.97]`}>
      <RefundSummaryCard
        cardType="indis-income"
        cardLabel={incomeItem?.businessInfo?.taxpayerName ?? ''}
        cardTitle={getCardTitle(isIncomeRetry, false, Number(incomeItem?.refundAmount ?? 0), applyableIncomeAmount, isAppliedIncomeRefund)}
        buttonState={getButtonState(isIncomeRetry, false, isAppliedIncomeRefund)}
        refundItem={incomeItem}
        cardAnimate={false}
      />
      {isAppliedIncomeRefund && incomeItem && (
        <>
          <div className={'px-6'}>
            <Divider.Line />
          </div>
          <StatusManualSection item={incomeItem} sectionType={'indis'} />
        </>
      )}
    </div>
  );

  // 양도소득세 카드
  const tritxCard = (
    <div className={`rounded-5 bg-white cursor-pointer transition-transform duration-150 ease-in-out hover:scale-[0.97]`}>
      <RefundSummaryCard
        cardType="indis-tritx"
        cardLabel={tritxItem?.businessInfo?.taxpayerName ?? ''}
        cardTitle={getCardTitle(isTritxRetry, false, Number(tritxItem?.refundAmount ?? 0), applyableTritxAmount, isAppliedTritxRefund)}
        buttonState={getButtonState(isTritxRetry, false, isAppliedTritxRefund, true)}
        refundItem={tritxItem}
        cardAnimate={false}
      />
      {isAppliedTritxRefund && tritxItem && (
        <>
          <div className={'px-6'}>
            <Divider.Line />
          </div>
          <StatusManualSection item={tritxItem as any} sectionType={'indis-tritx'} />
        </>
      )}
    </div>
  );

  const displayCardItems: JSX.Element[] = [];
  if (isShowCombinedCard) {
    displayCardItems.push(combinedCard);
  } else {
    if (isShowTritxFirst) displayCardItems.push(tritxCard);
    if (isShowIncomeCard) displayCardItems.push(incomeCard);
    if (!isShowTritxFirst && isShowTritxCard) displayCardItems.push(tritxCard);
  }

  return (
    <>
      {(incomeItem || tritxItem || previousItems?.length) && (
        <div>
          <Title.Section size="medium" title="개인·개인사업자" />
          {isShowNewYearLookupButton && <NewYearLookupButton handleClick={onClickLookup} />}
          {displayCardItems[0]}
          {displayCardItems[1] && (
            <>
              <div key="spacer" className="h-4" />
              {displayCardItems[1]}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default IndisSection;

const NewYearLookupButton = ({ handleClick }: { handleClick: () => void }) => {
  const { sendClickEvent } = useUserEventLogger();
  return (
    <div className="rounded-5 bg-white">
      <Guide>
        <Guide.ImageArea>
          <Image src="/images/illus-icons/apng-alarm.png" width={80} height={80} alt="coin-image" />
        </Guide.ImageArea>
        <Guide.TextArea>
          <Guide.Title titleType="small">{'올해의 새로운 환급금이 추가되었어요!'}</Guide.Title>
          <Guide.Description className={'text-13'}>{'지금 바로 조회해보세요!'}</Guide.Description>
        </Guide.TextArea>
        <Guide.ButtonArea className={'mb-0 w-full'}>
          <Button.Box
            onClick={() => {
              sendClickEvent('home_body_lookup-singleyear', { business: 'indis' });
              handleClick();
            }}
            state="primary"
            size={'small'}
            fullWidth
          >
            내 환급금 조회하기
          </Button.Box>
        </Guide.ButtonArea>
      </Guide>
    </div>
  );
};
